import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';

import { useActionTimeManagementFilters } from './use-filter';
import { getQueryKey } from './use-data';
import { showResult } from '../utils';

import type { ActionTimeManagementData, TSource } from './types';
import type { ActionTypes } from 'infrastructure/enums/action-types';

const endpoint = 'core-2/admin-tools/action-time-management';

export interface IUpdateActionTime {
  newTime: number;
  patientGuid: string;
  actionGuid: string;
  actionType: ActionTypes;
  module: TSource;
  reason: string;
}

export const useUpdateActionTimeManagement = () => {
  const queryClient = useQueryClient();
  const { filter } = useActionTimeManagementFilters();

  const queryKey = [...getQueryKey, filter] as const;

  const { mutate, data, isLoading, error } = useMutation<
    { message: string } | undefined,
    string | undefined,
    IUpdateActionTime
  >({
    mutationFn,
    onSuccess(_data, variables) {
      queryClient.setQueryData<ActionTimeManagementData>(
        queryKey,
        (oldData) => {
          if (!oldData) return oldData;

          const newData = structuredClone(oldData);

          newData.items.forEach((item) => {
            if (
              item.actionGuid === variables.actionGuid &&
              item.actionType === variables.actionType
            ) {
              item.time = variables.newTime;
            }
          });

          return newData;
        },
      );
      showResult();
    },
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return {
    updateActionTime: mutate,
    data,
    isLoading,
  };
};

const mutationFn = async (dataProps: IUpdateActionTime) => {
  const { error, data } = await dataService.updateOnly<{ message: string }>(
    endpoint,
    dataProps,
  );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};
